import { useLocalization } from "@fluent/react"
import { Alert, Box, Paper, Stack, Tab, Tabs, Typography } from "@mui/material"
import { useState } from "react"
import { CompanyDocumentWorkflowPanel } from "../administration/companyDocumentWorkflow/CompanyDocumentWorkflowPanel"
import { NodeReportTemplatesPanel } from "./reportTemplates/NodeReportTemplatesPanel"
import { StructureNodeTreeDto } from "./structureSlice"

type NodePanelTab = "workflow" | "report-templates"

interface StructureNodePanelProps {
    node: StructureNodeTreeDto | null
    isRoot: boolean
}

/** Right-hand panel of the org structure page: settings of the selected unit, one tab per area. */
export const StructureNodePanel = ({ node, isRoot }: StructureNodePanelProps) => {
    const { l10n } = useLocalization()
    // Kept while switching nodes, so an admin can walk the tree on the same tab.
    const [tab, setTab] = useState<NodePanelTab>("workflow")

    if (!node) {
        return (
            <Paper sx={{ p: 2, alignSelf: "start", borderStyle: "dashed", borderWidth: 1, borderColor: "divider" }}>
                <Alert severity="info">
                    {l10n.getString(
                        "structure-node-panel-select-hint",
                        null,
                        "Kliknij node w drzewku po lewej stronie, a tutaj pokażą się jego ustawienia.",
                    )}
                </Alert>
            </Paper>
        )
    }

    return (
        <Paper sx={{ p: 2, position: { md: "sticky" }, top: { md: 88 }, alignSelf: "start" }}>
            <Stack spacing={1.5}>
                <Box>
                    <Typography variant="overline" color="text.secondary">
                        {l10n.getString("structure-node-panel-title", null, "Element struktury")}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {node.code ? `${node.code} – ${node.name}` : node.name}
                    </Typography>
                </Box>

                <Tabs
                    value={tab}
                    onChange={(_, value: NodePanelTab) => setTab(value)}
                    variant="fullWidth"
                    sx={{ borderBottom: 1, borderColor: "divider" }}
                >
                    <Tab value="workflow" label={l10n.getString("structure-node-panel-tab-workflow", null, "Workflow")} />
                    <Tab value="report-templates" label={l10n.getString("structure-node-panel-tab-report-templates", null, "Szablony raportów")} />
                </Tabs>

                {tab === "workflow" && <CompanyDocumentWorkflowPanel node={node} embedded />}
                {tab === "report-templates" && <NodeReportTemplatesPanel node={node} isRoot={isRoot} />}
            </Stack>
        </Paper>
    )
}
